import {
  IonContent,
  IonPage,
  IonCard,
  IonCardHeader,
  IonCardTitle,
  IonCardContent,
  IonItem,
  IonLabel,
  IonInput,
  IonButton,
  IonText,
  IonIcon,
} from '@ionic/react';
import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLevel } from '../contexts/LevelContext';
import { lockClosed, card, logoPaypal, checkmarkCircle, informationCircle, gift } from 'ionicons/icons';
import { useNavigate } from 'react-router-dom';
import AppHeader from '../components/AppHeader';
import BottomNav from '../components/BottomNav';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import './PaymentInfo.css';

const PaymentInfo: React.FC = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { level } = useLevel();
  const [paymentMethod, setPaymentMethod] = useState<'paypal' | 'stripe' | 'giftcard' | ''>('');
  const [paypalEmail, setPaypalEmail] = useState('');
  const [stripeEmail, setStripeEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [saved, setSaved] = useState(false);

  // Load saved payment info
  useEffect(() => {
    const loadPaymentInfo = async () => {
      if (!currentUser) return;
      
      try {
        const userRef = doc(db, 'users', currentUser.uid);
        const userSnap = await getDoc(userRef);
        
        if (userSnap.exists()) {
          const data = userSnap.data();
          setPaymentMethod(data.paymentMethod || '');
          setPaypalEmail(data.paypalEmail || '');
          setStripeEmail(data.stripeEmail || '');
        }
      } catch (error) {
        console.error('Error loading payment info:', error);
      }
    };
    
    if (currentUser) {
      loadPaymentInfo();
    }
  }, [currentUser]);

  const handleSave = async () => {
    if (!currentUser) return;
    
    // If gift card is selected, navigate to redemption page
    if (paymentMethod === 'giftcard') {
      navigate('/gift-card-redemption');
      return;
    }
    
    if (paymentMethod === 'paypal' && !paypalEmail) {
      alert('Please enter your PayPal email');
      return;
    }
    
    if (paymentMethod === 'stripe' && !stripeEmail) {
      alert('Please enter your email for Stripe');
      return;
    }

    setLoading(true);
    try {
      const userRef = doc(db, 'users', currentUser.uid);
      await setDoc(userRef, {
        paymentMethod,
        paypalEmail: paymentMethod === 'paypal' ? paypalEmail : '',
        stripeEmail: paymentMethod === 'stripe' ? stripeEmail : '',
      }, { merge: true });
      
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (error) {
      console.error('Error saving payment info:', error);
      alert('Failed to save payment information. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const isLevel2Plus = (level || 1) >= 2;

  return (
    <IonPage>
      <AppHeader title="Payment Information" showBackButton={true} defaultHref="/wallet" />
      <IonContent fullscreen className="ion-padding">
        <div className="payment-info-container">
          {!isLevel2Plus ? (
            <IonCard className="level-notice-card">
              <IonCardContent>
                <div className="notice-header">
                  <IonIcon icon={lockClosed} className="notice-icon" />
                  <h3>Payment Information Unavailable</h3>
                </div>
                <p className="notice-text">
                  Cash payouts (PayPal/Stripe) are only available for Level 2+ users.
                  You are currently <strong>Level {level || 1}</strong>. Earn more coins to level up!
                </p>
                <div className="level-requirements">
                  <p className="requirements-title">Level Requirements:</p>
                  <div className="requirements-list">
                    <div className="requirement-item">
                      <span className="level-badge">Level 1</span>
                      <span className="requirement-text">0-500 lifetime earnings</span>
                    </div>
                    <div className="requirement-item">
                      <span className="level-badge">Level 2</span>
                      <span className="requirement-text">500+ lifetime earnings</span>
                    </div>
                    <div className="requirement-item">
                      <span className="level-badge">Level 3</span>
                      <span className="requirement-text">1,000+ lifetime earnings</span>
                    </div>
                    <div className="requirement-item">
                      <span className="level-badge">Level 4</span>
                      <span className="requirement-text">2,000+ lifetime earnings</span>
                    </div>
                    <div className="requirement-item">
                      <span className="level-badge">Level 5</span>
                      <span className="requirement-text">5,000+ lifetime earnings</span>
                    </div>
                    <div className="requirement-item">
                      <span className="level-badge">Level 6+</span>
                      <span className="requirement-text">10,000+ lifetime earnings</span>
                    </div>
                  </div>
                </div>
                <div className="notice-note">
                  <IonIcon icon={informationCircle} className="note-icon" />
                  <p>
                    <strong>Note:</strong> Levels are based on lifetime earnings (total coins ever earned), 
                    so redeeming coins won't lower your level!
                  </p>
                </div>
              </IonCardContent>
            </IonCard>
          ) : (
            <>
              <IonCard className="payment-form-card">
                <IonCardHeader>
                  <IonCardTitle>Payment Method</IonCardTitle>
                </IonCardHeader>
                <IonCardContent>
                  <div className="form-group">
                    <IonLabel className="input-label" style={{ marginBottom: '12px', display: 'block' }}>
                      Select Payment Method
                    </IonLabel>
                    <div className="payment-method-picker">
                      <div
                        className={`payment-method-card ${paymentMethod === 'giftcard' ? 'selected' : ''}`}
                        onClick={() => setPaymentMethod('giftcard')}
                      >
                        <div className="payment-method-icon-wrapper">
                          <IonIcon icon={gift} className="payment-method-icon" />
                        </div>
                        <div className="payment-method-content">
                          <h3 className="payment-method-title">Gift Cards</h3>
                          <p className="payment-method-subtitle">All Levels</p>
                        </div>
                        {paymentMethod === 'giftcard' && (
                          <IonIcon icon={checkmarkCircle} className="payment-method-check" />
                        )}
                      </div>
                      
                      {isLevel2Plus && (
                        <>
                          <div
                            className={`payment-method-card ${paymentMethod === 'paypal' ? 'selected' : ''}`}
                            onClick={() => setPaymentMethod('paypal')}
                          >
                            <div className="payment-method-icon-wrapper">
                              <IonIcon icon={logoPaypal} className="payment-method-icon" />
                            </div>
                            <div className="payment-method-content">
                              <h3 className="payment-method-title">PayPal</h3>
                              <p className="payment-method-subtitle">Level 2+</p>
                            </div>
                            {paymentMethod === 'paypal' && (
                              <IonIcon icon={checkmarkCircle} className="payment-method-check" />
                            )}
                          </div>
                          
                          <div
                            className={`payment-method-card ${paymentMethod === 'stripe' ? 'selected' : ''}`}
                            onClick={() => setPaymentMethod('stripe')}
                          >
                            <div className="payment-method-icon-wrapper">
                              <IonIcon icon={card} className="payment-method-icon" />
                            </div>
                            <div className="payment-method-content">
                              <h3 className="payment-method-title">Stripe</h3>
                              <p className="payment-method-subtitle">Credit/Debit Card</p>
                            </div>
                            {paymentMethod === 'stripe' && (
                              <IonIcon icon={checkmarkCircle} className="payment-method-check" />
                            )}
                          </div>
                        </>
                      )}
                    </div>

                    {paymentMethod === 'paypal' && (
                      <IonItem className="modern-input-item" lines="none">
                        <IonIcon icon={logoPaypal} slot="start" className="input-icon" />
                        <IonLabel position="stacked" className="input-label">
                          PayPal Email
                        </IonLabel>
                        <IonInput
                          type="email"
                          value={paypalEmail}
                          placeholder="your.email@example.com"
                          onIonInput={(e) => setPaypalEmail(e.detail.value as string)}
                          className="modern-input"
                        />
                      </IonItem>
                    )}

                    {paymentMethod === 'stripe' && (
                      <IonItem className="modern-input-item" lines="none">
                        <IonIcon icon={card} slot="start" className="input-icon" />
                        <IonLabel position="stacked" className="input-label">
                          Email for Stripe
                        </IonLabel>
                        <IonInput
                          type="email"
                          value={stripeEmail}
                          placeholder="your.email@example.com"
                          onIonInput={(e) => setStripeEmail(e.detail.value as string)}
                          className="modern-input"
                        />
                      </IonItem>
                    )}

                    {paymentMethod === 'giftcard' && (
                      <div className="gift-card-info">
                        <IonText color="medium">
                          <p className="gift-card-info-text">
                            Gift cards are available for all users! Click below to redeem your coins for gift cards.
                          </p>
                        </IonText>
                      </div>
                    )}

                    <IonButton
                      expand="block"
                      color="medium"
                      disabled={true}
                      className="save-button"
                      style={{ opacity: 0.5, cursor: 'not-allowed' }}
                      title="Coming Soon - Coin redemption will be available in a future update"
                    >
                      <IonIcon icon={gift} slot="start" />
                      Coming Soon
                    </IonButton>
                  </div>
                </IonCardContent>
              </IonCard>

              <IonCard className="info-card-modern">
                <IonCardContent>
                  <div className="info-note">
                    <IonIcon icon={lockClosed} className="info-note-icon" />
                    <div className="info-note-content">
                      <p className="info-note-title">Secure & Private</p>
                      <p className="info-note-text">
                        Your payment information is securely stored and will be used 
                        for processing cash payouts when you redeem coins. We never share your information 
                        with third parties.
                      </p>
                    </div>
                  </div>
                </IonCardContent>
              </IonCard>
            </>
          )}
        </div>
      </IonContent>
      <BottomNav />
    </IonPage>
  );
};

export default PaymentInfo;

